/**
 * Dialog for importing workflows from external organizations into the benchmark system.
 *
 * @docs-internal Superadmin-only benchmark tool
 * @module components/ImportWorkflowDialog
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import { Button, Dialog, DialogTrigger, DialogFooter, Select, Flex, Text, LoadingOrbit, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { DownloadSimple, CheckCircle } from '@phosphor-icons/react';
import { useOrganizationsQuery } from '../useSuperadminData';
import {
  useWorkflowsQuery,
  useImportWorkflowMutation,
} from './useImportWorkflowData';

export function ImportWorkflowDialog() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedOrgId, setSelectedOrgId] = React.useState<string | null>(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = React.useState<string | null>(null);

  const orgsQuery = useOrganizationsQuery({ pageSize: 500, sortBy: 'name', sortOrder: 'asc' });
  const workflowsQuery = useWorkflowsQuery(selectedOrgId);
  const importMutation = useImportWorkflowMutation();

  const orgOptions = React.useMemo(
    () =>
      (orgsQuery.data?.organizations ?? []).map((org) => ({
        id: org.id,
        label: org.name,
      })),
    [orgsQuery.data],
  );

  const workflowOptions = React.useMemo(
    () =>
      (workflowsQuery.data?.data ?? []).map((wf) => ({
        id: wf.id,
        label: wf.name,
        tag: wf.status === 'approved'
          ? ({ label: 'Approved', variant: 'success' as const })
          : wf.status === 'draft'
            ? ({ label: 'Draft', variant: 'secondary' as const })
            : undefined,
      })),
    [workflowsQuery.data],
  );

  const selectedWorkflow = React.useMemo(
    () => workflowsQuery.data?.data.find((wf) => wf.id === selectedWorkflowId),
    [workflowsQuery.data, selectedWorkflowId],
  );

  const handleOrgChange = (key: React.Key | null) => {
    setSelectedOrgId(key ? String(key) : null);
    setSelectedWorkflowId(null);
    importMutation.reset();
  };

  const handleWorkflowChange = (key: React.Key | null) => {
    setSelectedWorkflowId(key ? String(key) : null);
    importMutation.reset();
  };

  const handleImport = () => {
    if (!selectedWorkflowId) return;
    importMutation.mutate(selectedWorkflowId);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedOrgId(null);
      setSelectedWorkflowId(null);
      importMutation.reset();
    }
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange} size="lg">
      <Button variant="outline" iconLeft={<DownloadSimple size={16} />}>
        Import Flow
      </Button>
      <Dialog
        title="Import Workflow to Benchmark"
        description="Select an organization and workflow to copy into the benchmark system."
      >
        <Flex direction="column" gap="lg" className="mt-2">
          {/* Org selector */}
          {orgsQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-4">
              <LoadingOrbit size="md" />
            </Flex>
          ) : (
            <Select
              label="Organization"
              placeholder="Select an organization"
              items={orgOptions}
              selectedKey={selectedOrgId ?? undefined}
              onSelectionChange={handleOrgChange}
            />
          )}

          {/* Workflow selector */}
          {selectedOrgId && (
            workflowsQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-4">
                <LoadingOrbit size="md" />
              </Flex>
            ) : workflowsQuery.isError ? (
              <Text className="text-destructive">Failed to load workflows</Text>
            ) : workflowOptions.length === 0 ? (
              <Text className="text-muted-foreground">No workflows found for this organization.</Text>
            ) : (
              <Select
                label="Workflow"
                placeholder="Select a workflow"
                items={workflowOptions}
                selectedKey={selectedWorkflowId ?? undefined}
                onSelectionChange={handleWorkflowChange}
              />
            )
          )}

          {/* Selected workflow details */}
          {selectedWorkflow && (
            <div className="bg-muted/30 rounded-md p-3 space-y-1">
              <Flex gap="sm" align="center">
                <Text weight="medium">{selectedWorkflow.name}</Text>
                <TagBadge name={selectedWorkflow.status} color={badgeVariantToTagColor(selectedWorkflow.status === 'approved' ? 'success' : 'secondary')} hideDot />
              </Flex>
              <Text size="xs" className="text-muted-foreground">
                Type: {selectedWorkflow.flowType} | Updated: {new Date(selectedWorkflow.updatedAt).toLocaleDateString()}
              </Text>
            </div>
          )}

          {/* Error */}
          {importMutation.isError && (
            <Text className="text-destructive">{importMutation.error.message}</Text>
          )}

          {/* Success */}
          {importMutation.isSuccess && (
            <div className="rounded-md border border-success/30 bg-success/10 px-4 py-3 space-y-1">
              <Flex gap="sm" align="center">
                <CheckCircle size={18} className="text-success" />
                <Text className="text-success" weight="medium">
                  Imported successfully
                </Text>
              </Flex>
              <Text size="sm" className="text-muted-foreground">
                Created &quot;{importMutation.data.workflowName}&quot; in property &quot;{importMutation.data.propertyName}&quot;
                {importMutation.data.credentialMappings.length > 0 && (
                  <> with {importMutation.data.credentialMappings.length} credential(s) copied</>
                )}
              </Text>
            </div>
          )}
        </Flex>

        <DialogFooter>
          <Button variant="outline" onPress={() => handleOpenChange(false)}>
            {importMutation.isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!importMutation.isSuccess && (
            <Button
              onPress={handleImport}
              isDisabled={!selectedWorkflowId || importMutation.isPending}
            >
              {importMutation.isPending ? 'Importing...' : 'Import'}
            </Button>
          )}
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
